import { PieceType, Position } from "./types";
import { Piece } from "./Piece";
import { GameStateManager } from "./Managers/GameStateManager";
import { EventManager } from "./Managers/EventManager";
import { State, Mode } from "./types";
import { ZodiacEngine } from "./zodiacEngine";

export class SpawnAI {
  constructor(
    private gameStateManager: GameStateManager,
    private eventManager: EventManager,
    private engine: ZodiacEngine
  ) {}

  /**
   * Gets random positions for AI pieces in a specified row
   */
  getRandomPositions(count: number, row: number): Position[] {
    // Get 'count' random positions in the specified row
    const positions: Position[] = [];
    // The board is 5 columns (0-4), not 8
    const cols = [0, 1, 2, 3, 4];

    // Shuffle columns randomly
    for (let i = cols.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [cols[i], cols[j]] = [cols[j], cols[i]]; // Swap elements
    }

    // Take the first 'count' columns (or all if count > cols.length)
    const numToTake = Math.min(count, cols.length);
    for (let i = 0; i < numToTake; i++) {
      positions.push({ row, col: cols[i] });
    }

    return positions;
  }

  /**
   * Places random starting pieces for AI mode
   */
  placeRandomStartingPieces(lightKing: PieceType, darkKing: PieceType) {
    // Exclude Virus from eligible pieces for Play vs AI mode only
    const gameState = this.gameStateManager.getGameState();
    const eligiblePieces = [
      PieceType.Bear,
      PieceType.Crane,
      PieceType.Frog,
      PieceType.Fungi,
      PieceType.Goat,
      PieceType.Monkey,
      PieceType.Octopus,
      PieceType.Rhino,
      PieceType.Snake,
      PieceType.Tree,
      PieceType.Wolf,
    ];
    // If not in AI or Watch mode, allow Virus (for Dev or other modes)
    const eligiblePiecesWithVirus = [...eligiblePieces, PieceType.Virus];
    const usePieces =
      gameState.mode === Mode.AI || gameState.mode === Mode.Watch
        ? eligiblePieces
        : eligiblePiecesWithVirus;

    // Light pieces (row 0)
    const lightPositions = this.getRandomPositions(5, 0);
    const lightPiecesUsed: PieceType[] = [lightKing];
    const lightKingPosition =
      lightPositions[Math.floor(Math.random() * lightPositions.length)];
    // Use engine's createPiece for king
    const lightKingPiece = this.engine.createPiece(
      lightKing,
      true,
      lightKingPosition
    );
    lightKingPiece.isKing = true;
    let lightPiecesPlaced = 1;
    for (const position of lightPositions) {
      if (
        position.row === lightKingPosition.row &&
        position.col === lightKingPosition.col
      ) {
        continue;
      }
      if (lightPiecesPlaced < 5) {
        const availablePieces = usePieces.filter(
          (piece) => !lightPiecesUsed.includes(piece)
        );
        if (availablePieces.length === 0) break;
        const randomPiece =
          availablePieces[Math.floor(Math.random() * availablePieces.length)];
        // Use engine's createPiece for all pieces
        const piece = this.engine.createPiece(randomPiece, true, position);
        lightPiecesUsed.push(randomPiece);
        lightPiecesPlaced++;
      }
    }
    // Dark pieces (row 7)
    const darkPositions = this.getRandomPositions(5, 7);
    const darkPiecesUsed: PieceType[] = [darkKing];
    const darkKingPosition =
      darkPositions[Math.floor(Math.random() * darkPositions.length)];
    // Use engine's createPiece for king
    const darkKingPiece = this.engine.createPiece(
      darkKing,
      false,
      darkKingPosition
    );
    darkKingPiece.isKing = true;
    let darkPiecesPlaced = 1;
    for (const position of darkPositions) {
      if (
        position.row === darkKingPosition.row &&
        position.col === darkKingPosition.col
      ) {
        continue;
      }
      if (darkPiecesPlaced < 5) {
        const availablePieces = usePieces.filter(
          (piece) => !darkPiecesUsed.includes(piece)
        );
        if (availablePieces.length === 0) break;
        const randomPiece =
          availablePieces[Math.floor(Math.random() * availablePieces.length)];
        // Use engine's createPiece for all pieces
        const piece = this.engine.createPiece(randomPiece, false, position);
        darkPiecesUsed.push(randomPiece);
        darkPiecesPlaced++;
      }
    }
  }
}
